import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Play, RefreshCw } from "lucide-react";
import { api } from "../lib/api";
import type { Platform, Store, SyncJob } from "../lib/types";
import { dateTime, number, platformLabel, statusLabel } from "../lib/format";
import { StatusBadge, statusTone } from "../components/StatusBadge";
import { useI18n } from "../lib/i18n";

export function SyncJobsPage() {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const [platform, setPlatform] = useState<Platform>("amazon");
  const [storeId, setStoreId] = useState("");
  const [jobType, setJobType] = useState("orders");
  const jobs = useQuery({ queryKey: ["sync-jobs"], queryFn: () => api.get<{ jobs: SyncJob[] }>("/api/sync-jobs") });
  const stores = useQuery({ queryKey: ["stores"], queryFn: () => api.get<{ stores: Store[] }>("/api/stores") });
  const run = useMutation({
    mutationFn: () => api.post("/api/sync-jobs", { platform, storeId: storeId || null, jobType }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["sync-jobs"] });
      await queryClient.invalidateQueries({ queryKey: ["dashboard-summary"] });
    }
  });

  const filteredStores = (stores.data?.stores ?? []).filter((store) => store.platform === platform);

  return (
    <div className="page-stack">
      <section className="panel sync-console">
        <div className="panel-header">
          <div>
            <h2>{t("手动触发同步")}</h2>
            <span>{t("订单同步会读取平台数据并幂等写入订单中心。")}</span>
          </div>
        </div>
        <div className="sync-form">
          <label className="field">
            <span>{t("平台")}</span>
            <select value={platform} onChange={(event) => { setPlatform(event.target.value as Platform); setStoreId(""); }}>
              <option value="amazon">Amazon</option>
              <option value="tiktok">TikTok Shop</option>
              <option value="shein">SHEIN</option>
            </select>
          </label>
          <label className="field">
            <span>{t("店铺")}</span>
            <select value={storeId} onChange={(event) => setStoreId(event.target.value)}>
              <option value="">{t("全部店铺")}</option>
              {filteredStores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}
            </select>
          </label>
          <label className="field">
            <span>{t("任务类型")}</span>
            <select value={jobType} onChange={(event) => setJobType(event.target.value)}>
              <option value="orders">{t("订单同步")}</option>
              <option value="products">{t("商品同步")}</option>
              <option value="inventory">{t("库存同步")}</option>
            </select>
          </label>
          <button type="button" className="primary-button" onClick={() => run.mutate()} disabled={run.isPending}>
            <Play size={16} />
            {run.isPending ? t("执行中...") : t("执行同步")}
          </button>
        </div>
      </section>

      <div className="panel table-panel">
        <div className="panel-header">
          <div>
            <h2>{t("同步任务记录")}</h2>
            <span>{t("记录任务状态、数量、错误和最近执行结果。")}</span>
          </div>
          <button type="button" className="ghost-button" onClick={() => queryClient.invalidateQueries({ queryKey: ["sync-jobs"] })}>
            <RefreshCw size={15} />
            {t("刷新")}
          </button>
        </div>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>{t("平台")}</th>
                <th>{t("店铺名称")}</th>
                <th>{t("任务")}</th>
                <th>{t("状态")}</th>
                <th>{t("总数")}</th>
                <th>{t("成功")}</th>
                <th>{t("错误")}</th>
                <th>{t("开始时间")}</th>
                <th>{t("说明")}</th>
              </tr>
            </thead>
            <tbody>
              {(jobs.data?.jobs ?? []).map((job) => (
                <tr key={job.id}>
                  <td>{platformLabel(job.platform)}</td>
                  <td>{job.store_name || t("全部店铺")}</td>
                  <td>{job.job_type}</td>
                  <td><StatusBadge tone={statusTone(job.status)}>{t(statusLabel(job.status))}</StatusBadge></td>
                  <td>{number(job.total_count)}</td>
                  <td>{number(job.success_count)}</td>
                  <td className={job.error_count > 0 ? "danger-text" : ""}>{number(job.error_count)}</td>
                  <td>{dateTime(job.started_at)}</td>
                  <td className="message-cell">{t(job.message)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
